import ReactGA from 'react-ga';
import { removeCookie } from 'tiny-cookie';
import { AnalyticsBrowser } from '@segment/analytics-next';

import config from '../../config';
import { convertPathToName } from '../modules/formatting';
import { categories, PAGE_VIEWED } from '../constants/analytics';
import { isImpersonationSession } from './impersonationSession';

let segmentClient = null;

export const getSegmentClient = () => {
  if (segmentClient) return segmentClient;

  const segment = AnalyticsBrowser.load({ writeKey: config.segmentKey });

  segmentClient = segment;

  return segment;
};

export const initGA = () => {
  if (!window.GA_INITIALIZED) {
    ReactGA.initialize(config.googleAnalyticsId);
    window.GA_INITIALIZED = true;
  }
};

export const trackEvent = (event, props) => {
  const extendedProps = {
    ...props,
    'User Agent': navigator.userAgent,
    isImpersonationSession: isImpersonationSession(),
  };

  segmentClient.track(event, extendedProps);
  window?.userpilot?.track(event, extendedProps);
};

export const resetAnalytics = () => {
  segmentClient.reset();

  removeCookie('_cioanonid');
  removeCookie('_cioid');
  removeCookie('ajs_anonymous_id');
  removeCookie('ajs_user_id');

  localStorage.removeItem('ajs_user_traits');
  localStorage.removeItem('ajs_anonymous_id');
  localStorage.removeItem('ajs_user_id');
};

export const checkUserIsIdentified = async () => {
  const segmentUserMethods = await segmentClient.user();
  const segmentUser = await segmentUserMethods.traits();

  if (!segmentUser?.email) resetAnalytics();
};

export const trackPageViewed = url => {
  const category = categories.find(({ regex }) => url.match(regex));

  ReactGA.pageview(url);

  // Always track page views in Segment using the page() method
  // This ensures all pages are tracked, including listing pages
  const pageProperties = {
    path: url,
    url: typeof window !== 'undefined' ? window.location.href : url,
    referrer: typeof window !== 'undefined' ? document.referrer : '',
    ...(category && {
      Category: category.type,
      'Page Name': category.pageName || convertPathToName(url),
    }),
    ...(!category && {
      'Page Name': convertPathToName(url),
    }),
    'Page URL': url,
    Platform: 'Web',
    'User Agent': typeof navigator !== 'undefined' ? navigator.userAgent : '',
    isImpersonationSession: isImpersonationSession(),
  };

  // Get Segment client (initializes if not already initialized)
  // Segment will queue the page() call if not ready yet
  const client = getSegmentClient();
  if (client) {
    client.page(pageProperties);
  }

  // Also track the custom PAGE_VIEWED event for backward compatibility
  // Only if category exists (maintains existing behavior for categorized pages)
  // Remove User Agent and isImpersonationSession since trackEvent adds them automatically
  if (category) {
    const eventProperties = { ...pageProperties };
    delete eventProperties['User Agent'];
    delete eventProperties.isImpersonationSession;
    trackEvent(PAGE_VIEWED, eventProperties);
  }
};
